import { Skeleton } from "@/components/ui/skeleton";
import { Inbox, ChevronUp, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

export function SortTh({ label, k, sort, dir, onSort, right, className = "" }) {
  const active = sort === k;
  return (
    <th onClick={() => onSort(k)} data-testid={`sort-${k}`}
      className={`py-2 px-3 font-medium cursor-pointer select-none hover:text-slate-800 ${right ? "text-right" : ""} ${className}`}>
      <span className={`inline-flex items-center gap-1 ${right ? "justify-end w-full" : ""}`}>
        {label}
        {active && (dir === "desc" ? <ChevronDown size={13} /> : <ChevronUp size={13} />)}
      </span>
    </th>
  );
}

export function useTableSort(rows, initialKey, initialDir = "asc", accessors = {}) {
  const [sort, setSort] = useState(initialKey);
  const [dir, setDir] = useState(initialDir);
  const onSort = (k) => {
    if (sort === k) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setDir("asc"); }
  };
  const sorted = useMemo(() => {
    if (!rows) return rows;
    const get = accessors[sort] || ((r) => r[sort]);
    const copy = [...rows];
    copy.sort((a, b) => {
      let va = get(a), vb = get(b);
      if (va == null) va = "";
      if (vb == null) vb = "";
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    });
    return dir === "desc" ? copy.reverse() : copy;
  }, [rows, sort, dir]); // eslint-disable-line
  return { sorted, sort, dir, onSort };
}


export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section className={`panel p-4 sm:p-5 ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-head text-base font-semibold tracking-tight">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function StatCard({ label, value, unit, sub, accent = "#7C3AED", icon: Icon, testid }) {
  return (
    <div className="panel p-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent }} />
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        {Icon && <Icon size={16} style={{ color: accent }} />}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="kpi-value text-2xl sm:text-3xl text-slate-900">{value}</span>
        {unit && <span className="text-sm text-slate-400 font-medium">{unit}</span>}
      </div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function LoadingCards({ n = 4 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="panel p-4">
          <Skeleton className="h-3 w-24 mb-3" />
          <Skeleton className="h-8 w-32" />
        </div>
      ))}
    </div>
  );
}

export function LoadingPanel({ h = 260 }) {
  return (
    <div className="panel p-5">
      <Skeleton className="h-4 w-40 mb-4" />
      <Skeleton className="w-full" style={{ height: h }} />
    </div>
  );
}

export function EmptyState({ message = "No data for the current filters.", onReset }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="empty-state">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Inbox size={22} className="text-slate-400" />
      </div>
      <p className="text-sm text-slate-500">{message}</p>
      {onReset && (
        <button onClick={onReset} className="mt-3 text-sm font-semibold text-violet-700 hover:underline" data-testid="empty-reset-btn">
          Reset filters
        </button>
      )}
    </div>
  );
}

export function MachineBadge({ code }) {
  const map = {
    L1: { bg: "#EDE9FE", c: "#6D28D9" },
    L2: { bg: "#CCFBF1", c: "#0F766E" },
    L3: { bg: "#FEF3C7", c: "#B45309" },
    UNALLOCATED: { bg: "#F1F5F9", c: "#475569" },
  };
  const s = map[code] || map.UNALLOCATED;
  return (
    <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded" style={{ background: s.bg, color: s.c }}>
      {code === "UNALLOCATED" ? "UNALLOC" : code}
    </span>
  );
}
